import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../../components/ui/Icon';
import { Chip } from '../../../components/ui/Chip';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SkeletonTable } from '../../../components/ui/Skeleton';
import { Pagination } from '../../../components/ui/Pagination';
import { MobileFilters } from '../../../components/ui/MobileFilters';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../../lib/utils';
import {
  METODO_LABEL, TIPO_SPESA_LABEL, durata, giorniDaOggi, meseInParole, nomeBreve, spesePagateQuery,
  type SpesaPagata, type SpesePagateData, type TipoSpesa,
} from '../fatture/fattureApi';

/**
 * Spese › Spese già pagate (proposta § 2.3): lo storico, con ricerca e mese.
 *
 * Fatture dei fornitori segnate pagate, spese fisse e contanti registrati come
 * pagati; SENZA tasse (Fisco) e SENZA stipendi (Dipendenti). Il totale
 * dell'anno sta nella fascia di Spese da pagare ("Pagato ai fornitori"): qui
 * non si ripete. Si vede una somma solo quando si sceglie un mese o si cerca,
 * perché allora è un numero diverso.
 */
const TIPO_TONE: Record<TipoSpesa, 'default' | 'info' | 'ghost'> = { FORNITORE: 'default', SPESA_FISSA: 'info', CONTANTI: 'ghost' };
const PAGE_SIZE = 25;

export function SpesePagate({ showHistory, tabs }: { showHistory?: boolean; tabs: ReactNode }) {
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [mese, setMese] = useUrlState<string>('mese', '');
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, mese, showHistory]);
  const q = useQuery(spesePagateQuery({ search, month: mese || undefined, includeHistory: showHistory, page, pageSize: PAGE_SIZE }));
  const data = q.data;
  const filtrato = !!search.trim() || !!mese;

  return (
    <>
      {data && <p className="fs-frase">{frase(data, mese, filtrato)}</p>}

      {tabs}

      <MobileFilters attivi={[search.trim(), mese].filter(Boolean).length}>
        <div className="filterbar filterbar--float mb-4 flex-wrap">
          <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
            <Icon name="search" size={12} />
            <input aria-label="Cerca fra le spese già pagate" placeholder="Cerca per fornitore, spesa o numero di fattura…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="inp inp-sm" style={{ width: 220 }} aria-label="Mese del pagamento" value={mese} onChange={(e) => setMese(e.target.value)}>
            <option value="">Pagate in tutti i mesi</option>
            {(data?.mesi ?? []).map((m) => (
              <option key={m.month} value={m.month}>Pagate a {meseInParole(m.month)} ({m.count})</option>
            ))}
          </select>
          <span className="mute text-[12px]">{(data?.total ?? 0) === 1 ? '1 spesa pagata' : `${data?.total ?? 0} spese pagate`}</span>
        </div>
      </MobileFilters>

      {q.isLoading && !data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={8} cols={5} withAvatar={false} /></tbody></table></div>
      ) : !data || data.items.length === 0 ? (
        filtrato ? (
          <EmptyState brand title="Nessuna spesa pagata con questi filtri" description="Prova con un altro mese o con il nome del fornitore."
            action={<button className="btn" onClick={() => { setSearch(''); setMese(''); }}>Azzera i filtri</button>} />
        ) : (
          <EmptyState brand title="Nessuna spesa pagata" description="Le spese segnate come pagate, da qui o da FattureInCloud, compaiono qui." />
        )
      ) : (
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">{mese ? `Spese pagate a ${meseInParole(mese)}` : 'Spese già pagate, la più recente in cima'}</span>
            {filtrato && <span className="mono mute text-[12px]">in tutto € {fmtEur(data.totals.amount, true)}</span>}
          </div>
          <table className="tbl tbl--ed tbl--card tbl--static">
            <thead>
              <tr>
                <th>Pagata a</th>
                <th style={{ width: 150 }}>Tipo di spesa</th>
                <th style={{ width: 150 }}>Pagata il</th>
                <th style={{ width: 180 }}>Pagata con</th>
                <th className="num" style={{ width: 130 }}>Importo</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => <RigaPagata key={r.key} r={r} />)}
            </tbody>
          </table>
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} totalPages={data.totalPages} onPage={setPage} />
        </div>
      )}
    </>
  );
}

function frase(d: SpesePagateData, mese: string, filtrato: boolean): ReactNode {
  if (filtrato) {
    return d.total === 0
      ? <>Con questi filtri non risulta nessun pagamento.</>
      : <>{mese ? <>A {meseInParole(mese)} risultano </> : <>Con questa ricerca risultano </>}<strong>{d.total === 1 ? '1 pagamento' : `${d.total} pagamenti`}</strong>.</>;
  }
  const ultima = d.items[0];
  if (!ultima) return <>Con i dati di oggi non risulta nessun pagamento ai fornitori.</>;
  const g = -(giorniDaOggi(ultima.paidDate) ?? 0);
  const quandoPagata = g <= 0 ? 'oggi' : g === 1 ? 'ieri' : `${durata(g)} fa`;
  return <>L'ultimo pagamento registrato è {nomeBreve(ultima.counterparty)}, {quandoPagata}.</>;
}

function RigaPagata({ r }: { r: SpesaPagata }) {
  const dettaglio = [r.number ? `fattura n. ${r.number}` : null, r.description].filter(Boolean).join(' · ');
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="hd" className="adm-clip">
        <div className="font-semibold flex items-center gap-2">
          <span className="truncate">{r.counterparty}</span>
          {r.entity === 'PERSONALE_ANDREA' && <Chip tone="ghost">Di Andrea</Chip>}
        </div>
        {dettaglio && <div className="text-[12px] mute" title={dettaglio}>{dettaglio}</div>}
        {r.localPaidAt && (
          <div className="fs-sugg">
            <Chip tone="ghost"><span title="Da comunicare al commercialista: la trovi in Banca">Segnata pagata qui, su FattureInCloud ancora da pagare</span></Chip>
          </div>
        )}
      </td>
      <td data-m="key" data-l="Tipo di spesa"><Chip tone={TIPO_TONE[r.tipo]}>{TIPO_SPESA_LABEL[r.tipo]}</Chip></td>
      <td data-m="key" data-l="Pagata il" className="mono mute">{fmtDate(r.paidDate)}</td>
      <td data-m="off" className="mute">{r.method ? METODO_LABEL[r.method] : '—'}</td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.amount, true)}</td>
    </tr>
  );
}
